import React, { useState, useCallback } from 'react';
import {
    Search, X, Columns2, Link as LinkIcon, Check,
} from 'lucide-react';
import {
    useSetRelatedContentsMutation,
} from '../../generated/graphql';
import ContentDetailDialog from '../../components/dialogs/ContentDetailDialog/ContentDetailDialog';
import ContentSearchPane from './ContentSearchPane';

type ContentSearchProps = {
    style?: React.CSSProperties;
};

// ─── Main Component ──────────────────────────────────────────────
const ContentSearch = (props: ContentSearchProps) => {
    const { style } = props;

    // ── Split view state ─────────────────────────────────────────
    const [splitView, setSplitView] = useState(false);

    // ── Content detail dialog state ──────────────────────────────
    const [detailContentId, setDetailContentId] = useState<string | null>(null);
    const [detailClientId, setDetailClientId] = useState<string | null>(null);

    // ── Lightbox state ───────────────────────────────────────────
    const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);

    // ── Drag-over highlight per pane ─────────────────────────────
    const [dragOverPane, setDragOverPane] = useState<string | null>(null);

    // ── Link toast ───────────────────────────────────────────────
    const [linkToast, setLinkToast] = useState<{ sourceName: string; targetPane: string } | null>(null);

    // ── Mutation ─────────────────────────────────────────────────
    const [setRelatedContents] = useSetRelatedContentsMutation();

    const handleOpenDetail = useCallback((contentId: string, clientId: string) => {
        setDetailContentId(contentId);
        setDetailClientId(clientId);
    }, []);

    const handleDrop = useCallback(async (sourceContentId: string, sourceContentName: string, targetPaneId: string) => {
        // In split view, dropping onto a pane means:
        // "Link the dragged content TO every visible item in the target pane" is too aggressive.
        // Instead, we need a target. Let's use a simple approach:
        // The pane's drop zone itself acts as a "link to this pane's last clicked/selected item".
        // Actually, the cleanest UX: we prompt which item to link to? No — that breaks the drag flow.
        //
        // Best approach: cards are draggable AND droppable. When you drop card A onto card B,
        // we link A→B and B→A. The pane-level drop is a fallback that does nothing.
        // But the pane-level drop IS still useful as a visual guide.
        //
        // For simplicity in v1: any card dropped onto the opposing pane creates a one-way link.
        // The user can then drop from the other direction too.
        // We store the target content ID in the dataTransfer via the card-level drop handler below.
        //
        // Actually, the HTML5 drag API doesn't let us read dataTransfer during dragOver (security).
        // So we use the pane-level approach: when you drop a card onto a CARD in the other pane,
        // the card's own onDrop fires first. Let's implement card-to-card linking.

        // This is the pane-level fallback — show a hint toast
        setLinkToast({ sourceName: sourceContentName, targetPane: targetPaneId });
        setTimeout(() => setLinkToast(null), 3000);
    }, []);

    // Card-to-card drop handler — stores the link on the SOURCE side only.
    // The dragged card is the source; it gets the target added to its relatedContentIds.
    // Reverse lookups ("what links to X") can be done via a DB query on relatedContentIds.
    const handleCardDrop = useCallback(async (sourceContentId: string, sourceContentName: string, targetContentId: string, targetContentName: string) => {
        try {
            await setRelatedContents({ variables: { id: sourceContentId, ids: [targetContentId] } });
            setLinkToast({ sourceName: `${sourceContentName} → ${targetContentName}`, targetPane: 'linked' });
        } catch (err) {
            console.error('Failed to link content:', err);
            setLinkToast({ sourceName: `Failed to link — you may not have admin access on "${sourceContentName}"'s library`, targetPane: 'error' });
        }
        setTimeout(() => setLinkToast(null), 3000);
    }, [setRelatedContents]);

    // ═════════════════════════════════════════════════════════════
    // RENDER
    // ═════════════════════════════════════════════════════════════
    return (
        <div className="page-container" style={style} data-testid="ContentSearch">

            {/* ─── Header ────────────────────────────────── */}
            <div className="page-header">
                <div className="flex items-center justify-between w-full">
                    <div>
                        <h1 className="page-title flex items-center gap-3">
                            <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: 'var(--accent-soft)' }}>
                                <Search size={20} style={{ color: 'var(--accent)' }} />
                            </div>
                            Content Search
                        </h1>
                        <p className="page-subtitle" style={{ marginLeft: '52px' }}>
                            {splitView
                                ? 'Drag content cards between panes to create links'
                                : 'Search and filter to find content across all libraries'
                            }
                        </p>
                    </div>
                    <button
                        className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold transition-all"
                        style={{
                            backgroundColor: splitView ? 'var(--accent)' : 'var(--surface-1)',
                            color: splitView ? 'white' : 'var(--text-secondary)',
                            border: splitView ? '1px solid var(--accent)' : '1px solid var(--border-default)',
                            cursor: 'pointer',
                            boxShadow: splitView ? '0 0 0 3px var(--accent-soft)' : 'none',
                        }}
                        onClick={() => setSplitView(prev => !prev)}
                        title={splitView ? 'Exit split view' : 'Split view — drag to link content'}
                    >
                        {splitView ? <X size={16} /> : <Columns2 size={16} />}
                        {splitView ? 'Exit Split View' : 'Split & Link'}
                    </button>
                </div>
            </div>

            {/* ─── Panes ─────────────────────────────────── */}
            {splitView ? (
                <div className="flex gap-3 flex-1 min-h-0" style={{ minHeight: 0 }}>
                    {/* Left pane */}
                    <div className="flex-1 flex flex-col min-w-0 rounded-xl overflow-hidden" style={{ border: '1px solid var(--border-default)' }}>
                        <div className="px-4 py-2 flex items-center gap-2" style={{ backgroundColor: 'var(--surface-1)', borderBottom: '1px solid var(--border-default)' }}>
                            <div className="w-2 h-2 rounded-full" style={{ backgroundColor: 'var(--accent)' }} />
                            <span className="text-xs font-semibold uppercase tracking-wider" style={{ color: 'var(--text-tertiary)' }}>Pane A</span>
                            <span className="text-xs ml-auto" style={{ color: 'var(--text-tertiary)' }}>Drag cards →</span>
                        </div>
                        <div className="flex-1 p-3 overflow-auto" style={{ backgroundColor: 'var(--bg-page)' }}>
                            <ContentSearchPane
                                paneId="left"
                                splitMode
                                onDrop={handleDrop}
                                onOpenDetail={handleOpenDetail}
                                onZoom={setLightboxUrl}
                                isDragOver={dragOverPane === 'left'}
                                onDragOverChange={(over) => setDragOverPane(over ? 'left' : null)}
                                onCardDrop={handleCardDrop}
                            />
                        </div>
                    </div>

                    {/* Divider with link icon */}
                    <div className="flex flex-col items-center justify-center gap-2" style={{ width: 40 }}>
                        <div className="w-px flex-1" style={{ backgroundColor: 'var(--border-default)' }} />
                        <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: 'var(--accent-soft)', border: '1px solid var(--accent)' }}>
                            <LinkIcon size={16} style={{ color: 'var(--accent)' }} />
                        </div>
                        <div className="w-px flex-1" style={{ backgroundColor: 'var(--border-default)' }} />
                    </div>

                    {/* Right pane */}
                    <div className="flex-1 flex flex-col min-w-0 rounded-xl overflow-hidden" style={{ border: '1px solid var(--border-default)' }}>
                        <div className="px-4 py-2 flex items-center gap-2" style={{ backgroundColor: 'var(--surface-1)', borderBottom: '1px solid var(--border-default)' }}>
                            <div className="w-2 h-2 rounded-full" style={{ backgroundColor: 'var(--warning)' }} />
                            <span className="text-xs font-semibold uppercase tracking-wider" style={{ color: 'var(--text-tertiary)' }}>Pane B</span>
                            <span className="text-xs ml-auto" style={{ color: 'var(--text-tertiary)' }}>← Drag cards</span>
                        </div>
                        <div className="flex-1 p-3 overflow-auto" style={{ backgroundColor: 'var(--bg-page)' }}>
                            <ContentSearchPane
                                paneId="right"
                                splitMode
                                onDrop={handleDrop}
                                onOpenDetail={handleOpenDetail}
                                onZoom={setLightboxUrl}
                                isDragOver={dragOverPane === 'right'}
                                onDragOverChange={(over) => setDragOverPane(over ? 'right' : null)}
                                onCardDrop={handleCardDrop}
                            />
                        </div>
                    </div>
                </div>
            ) : (
                /* Single pane mode */
                <ContentSearchPane
                    paneId="single"
                    onOpenDetail={handleOpenDetail}
                    onZoom={setLightboxUrl}
                />
            )}

            {/* ─── Link toast notification ─────────────────── */}
            {linkToast && (
                <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[200] animate-fade-in"
                    style={{ pointerEvents: 'none', maxWidth: '90vw' }}>
                    <div className="flex items-center gap-2.5 px-5 py-3 rounded-xl"
                        style={{
                            backgroundColor: linkToast.targetPane === 'error' ? 'var(--danger, #ef4444)' : 'var(--accent)',
                            color: 'white',
                            boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
                        }}>
                        {linkToast.targetPane === 'error' ? <X size={16} /> : <Check size={16} />}
                        <span className="text-sm font-medium">
                            {linkToast.targetPane === 'linked'
                                ? `Linked: ${linkToast.sourceName}`
                                : linkToast.targetPane === 'error'
                                    ? linkToast.sourceName
                                    : `Drop onto a specific card to link "${linkToast.sourceName}"`
                            }
                        </span>
                    </div>
                </div>
            )}

            {/* ─── Content Detail Dialog ──────────────────── */}
            {detailContentId && detailClientId && (
                <ContentDetailDialog
                    visible={true}
                    contentId={detailContentId}
                    clientId={detailClientId}
                    onClose={() => { setDetailContentId(null); setDetailClientId(null); }}
                />
            )}

            {/* ─── Lightbox overlay ────────────────────────── */}
            {lightboxUrl && (
                <div className="fixed inset-0 flex items-center justify-center"
                    style={{ backgroundColor: 'rgba(0,0,0,0.75)', zIndex: 9999 }}
                    onClick={() => setLightboxUrl(null)}>
                    <div className="relative" onClick={e => e.stopPropagation()}>
                        <img src={lightboxUrl} alt="Full size" style={{ maxWidth: '90vw', maxHeight: '85vh', borderRadius: 8 }} />
                        <button onClick={() => setLightboxUrl(null)}
                            className="absolute top-2 right-2 w-8 h-8 rounded-full flex items-center justify-center"
                            style={{ backgroundColor: 'rgba(0,0,0,0.5)', color: 'white', cursor: 'pointer', border: 'none' }}>
                            <X size={16} />
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ContentSearch;
